import { useCallback } from 'react';
import type {
  PropsWithChildren,
  ReactElement,
} from 'react';

import styled from 'styled-components';

import { Dropdown } from '../Dropdown';
import type {
  DropdownContentRenderProps,
  DropdownRenderProps,
} from '../Dropdown';

import { PageSizeSelectMenu } from './PageSizeSelectMenu';

export const StyledButton = styled.button<{
  $isDropdownOpen: boolean;
}>(({
  $isDropdownOpen,

  theme: {
    colors,
    pageSizeSelect,
  },
}) => ({
  ...pageSizeSelect.BUTTON_FONT,
  color: $isDropdownOpen ? colors.ACTION_PRESSED : colors.ACTION_DEFAULT,
  margin: 0,
  padding: 0,
  outline: 'none',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',

  ':hover': $isDropdownOpen ? undefined : {
    color: colors.ACTION_HOVER,
  },

  ':active': $isDropdownOpen ? undefined : {
    color: colors.ACTION_PRESSED,
  },
}));

export type PageSizeSelectDropdownProps = PropsWithChildren<{
  value: number;
  options: number[];

  onChange: (nextValue: number) => void;

  toggleButtonId: string;
}>;

export function PageSizeSelectDropdown({
  value,
  options,
  children = undefined,
  onChange,
  toggleButtonId,
}: PageSizeSelectDropdownProps): ReactElement {
  const renderReference = useCallback(({
    isDropdownOpen,
    onClick,
    ref,
  }: DropdownRenderProps) => (
    <StyledButton
      id={toggleButtonId}
      type="button"
      $isDropdownOpen={isDropdownOpen}
      onClick={onClick}
      ref={ref}
    >
      {children}
    </StyledButton>
  ), [
    children,
    toggleButtonId,
  ]);

  const renderContent = useCallback(({
    close,
  }: DropdownContentRenderProps) => (
    <PageSizeSelectMenu
      value={value}
      options={options}
      onChange={onChange}
      close={close}
    />
  ), [value, options, onChange]);

  return (
    <Dropdown
      renderContent={renderContent}
    >
      {renderReference}
    </Dropdown>
  );
}
